import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Building, Home, Mail, Plus, Search, Settings } from "lucide-react";
import { useTranslate } from "ra-core";
import { Link, matchPath, useLocation, useMatch } from "react-router";
import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { useState } from "react";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { LeadQuickCreate } from "../deals/LeadQuickCreate";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/email/*", location.pathname)) {
    currentPath = "/email";
  } else {
    currentPath = false;
  }

  // Check if the app is running as a PWA (standalone mode)
  const isPwa = window.matchMedia("(display-mode: standalone)").matches;
  // Check if it's iOS on the web
  const isWebiOS = /iPad|iPod|iPhone/.test(window.navigator.userAgent);

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 border-t bg-secondary h-14"
      style={{
        // iOS bug: even though viewport is set correctly, the bottom safe area inset is not accounted for
        // So we manually add some padding to avoid the navigation being too close to the home bar
        paddingBottom: isPwa && isWebiOS ? 15 : undefined,
        // We use box-sizing: border-box, so the height contains the padding.
        // To actually increase the padding, we need to increase the height as well
        // (3.5rem = h-14; tidligere calc hadde «* 6» utenfor calc() og ble
        // forkastet av browseren, så safe-area-høyden virket aldri).
        height: isPwa && isWebiOS ? "calc(3.5rem + 15px)" : undefined,
      }}
    >
      <div className="flex items-center justify-around w-full px-1">
        <>
          <NavigationButton
            href="/"
            Icon={Home}
            // Korte etiketter: «Oversikt» og «Innstillinger» ble kuttet til
            // «Over…» og «Innsti…» i bunnmenyen.
            label={translate("crm.navigation.home", { _: "Hjem" })}
            isActive={currentPath === "/"}
          />
          <NavigationButton
            href="/companies"
            Icon={Building}
            label={translate("resources.companies.name", {
              smart_count: 2,
            })}
            isActive={currentPath === "/companies"}
          />
          <CreateButton />
          <SearchButton />
          <NavigationButton
            href="/email"
            Icon={Mail}
            label={translate("crm.email.title_page", { _: "E-post" })}
            isActive={currentPath === "/email"}
          />
          <SettingsButton />
        </>
      </div>
    </nav>
  );
};

// Mobilens søkeinngang (PR-S2): åpner Cmd+K-paletten (globalsøket) via samme
// hendelse som søkeboksen i desktop-toppbaren.
const SearchButton = () => {
  const translate = useTranslate();
  return (
    <Button
      variant="ghost"
      onClick={() => window.dispatchEvent(new Event("mimin:open-command"))}
      className="flex-col gap-1 h-auto min-h-11 min-w-11 py-2 px-1 has-[>svg]:px-1 rounded-md flex-1 max-w-16 text-muted-foreground"
    >
      <Search className="size-6 shrink-0" />
      <span className="text-[10px] font-medium truncate max-w-full">
        {translate("crm.command.search", { _: "Søk" })}
      </span>
    </Button>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      "flex-col gap-1 h-auto min-h-11 min-w-11 py-2 px-1 has-[>svg]:px-1 rounded-md flex-1 max-w-16",
      isActive ? "text-primary" : "text-muted-foreground",
    )}
  >
    <Link to={href} aria-current={isActive ? "page" : undefined}>
      <Icon className="size-6 shrink-0" />
      <span className="text-[10px] font-medium truncate max-w-full">
        {label}
      </span>
    </Link>
  </Button>
);

const CreateButton = () => {
  const translate = useTranslate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  // Ny lead etter en samtale (kap. 17.6, AK39).
  const [leadCreateOpen, setLeadCreateOpen] = useState(false);

  return (
    <>
      <ContactCreateSheet
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
      />
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <LeadQuickCreate open={leadCreateOpen} onOpenChange={setLeadCreateOpen} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            size="icon"
            className="h-16 w-16 rounded-full -mt-3"
            aria-label={translate("ra.action.create")}
          >
            <Plus className="size-10" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setLeadCreateOpen(true);
            }}
          >
            {translate("crm.deals.new_lead", { _: "Ny lead" })}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setContactCreateOpen(true);
            }}
          >
            {translate("resources.contacts.forcedCaseName")}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="h-12 px-4 text-base"
            onSelect={() => {
              setNoteCreateOpen(true);
            }}
          >
            {translate("resources.notes.forcedCaseName")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

const SettingsButton = () => {
  const translate = useTranslate();
  const location = useLocation();
  const isActive = !!matchPath("/settings", location.pathname);

  return (
    <NavigationButton
      href="/settings"
      Icon={Settings}
      label={translate("crm.navigation.more", { _: "Mer" })}
      isActive={isActive}
    />
  );
};
